// frontend-booking/src/components/web-booking/TakeoutPanel.js
// ============================================================
// 外帶自取面板（Plan 106，版型 B 定稿）— 掛在公開訂位頁 /r/:slug 的「外帶自取」tab 下
//
//   菜單瀏覽（左側分類欄 sticky＋scroll-spy、品項卡、購物車 bar）
//   → 品項詳情 bottom sheet（份量 radio＋加購＋數量，自適應高度）
//   → 結帳（明細可改量、＋加點其他菜色、取餐日期/時段、姓名/手機、匯款資訊＋後五碼）
//   → 完成頁（大字取餐代號）
//
// 配色：按鈕黑白（#0f172a），與訂位側綠色區隔；分類欄目前分類淡綠底＋綠左槓。
// 金額只做前端顯示，送單後端一律重算（takeout.service）。
// ============================================================

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Stack,
  CircularProgress, Alert, Drawer, Radio, Checkbox, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import bookingService from '../../services/bookingService';

const INK = '#0f172a';        // 主按鈕（黑）
const INK_HOVER = '#1e293b';
const GREEN = '#06C755';      // 分類欄 active accent（沿用品牌綠）
const GREEN_BG = '#e3f6ea';
const COMPANY = '捷思博科技股份有限公司';

const NAV_OFFSET = 80;        // sticky tab 列高度（76px；TopBar 已改非 sticky，IG 式只留一條），scroll-spy / 錨點捲動用

// ---- 工具 ----
const money = (n) => `NT$${Number(n || 0).toLocaleString()}`;
const todayTaipei = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
const addDaysISO = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const dateLabel = (iso, today) => {
  const off = Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / 86400000);
  const [, m, d] = iso.split('-');
  const md = `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  if (off === 0) return `今天 ${md}`;
  if (off === 1) return `明天 ${md}`;
  if (off === 2) return `後天 ${md}`;
  return `${md}（週${WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()]}）`;
};

// 品項單價（份量價 + 加購合計）— 僅前端顯示，後端建單重算
const unitPriceOf = (item, variantLabel, optionLabels) => {
  const po = item.priceOptions || [];
  const variant = po.length === 1 ? po[0] : po.find((p) => (p.label || '') === (variantLabel || ''));
  const base = variant ? variant.price : 0;
  const flat = (item.optionGroups || []).flatMap((g) => g.options || []);
  const extra = (optionLabels || []).reduce((s, l) => {
    const hit = flat.find((o) => o.label === l);
    return s + (hit ? hit.price : 0);
  }, 0);
  return base + extra;
};

const priceText = (item) => {
  const po = item.priceOptions || [];
  if (!po.length) return '';
  return po.map((p) => (p.label ? `${p.label} ${money(p.price)}` : money(p.price))).join('／');
};

// 購物車行識別（同品項＋同份量＋同加購 = 同一行）
const lineKey = (itemId, variantLabel, options) =>
  `${itemId}|${variantLabel || ''}|${[...(options || [])].sort().join(',')}`;

const ERROR_MESSAGES = {
  BAD_REQUEST: '請填寫完整資料',
  BAD_PHONE: '電話格式不正確，請重新輸入',
  BAD_LAST5: '匯款後五碼需為 5 位數字',
  BAD_TIME: '取餐時間已不可選（可能已過最短備餐時間），請重新選擇',
  OUT_OF_WINDOW: '取餐日期不在可選範圍',
  CLOSED_DATE: '所選日期店家公休，請另選日期',
  BAD_ITEM: '訂單內有品項已下架或調整，請返回菜單重新確認',
  MARKET_ITEM: '訂單內有時價品項，請來電訂購',
  BAD_OPTION: '訂單內有加購選項已調整，請返回菜單重新確認',
  RATE_LIMITED: '操作太頻繁，請稍候再試',
  SERVER_ERROR: '系統忙線中，請稍後再試'
};

// 小型步進器（黑白）
const QtyStepper = ({ value, min = 1, max = 99, onChange, small }) => (
  <Stack direction="row" alignItems="center" spacing={small ? 0.5 : 1}>
    <IconButton size="small" disabled={value <= min} onClick={() => onChange(value - 1)}
      sx={{ border: '1px solid', borderColor: 'divider', width: small ? 30 : 36, height: small ? 30 : 36 }}>
      <RemoveIcon sx={{ fontSize: small ? 16 : 20 }} />
    </IconButton>
    <Typography sx={{ minWidth: small ? 22 : 28, textAlign: 'center', fontSize: small ? 16 : 18, fontWeight: 700 }}>{value}</Typography>
    <IconButton size="small" disabled={value >= max} onClick={() => onChange(value + 1)}
      sx={{ border: '1px solid', borderColor: 'divider', width: small ? 30 : 36, height: small ? 30 : 36 }}>
      <AddIcon sx={{ fontSize: small ? 16 : 20 }} />
    </IconButton>
  </Stack>
);

const Spicy = ({ level }) => (level > 0 ? <span aria-label={`辣度 ${level}`}>{'🌶️'.repeat(Math.min(level, 3))}</span> : null);

const TakeoutPanel = ({ slug, restaurant }) => {
  const today = todayTaipei();

  // ---- 資料 ----
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [takeoutCfg, setTakeoutCfg] = useState(null); // { bankInfo, leadMinutes, slotMinutes, windowDays, notice }

  // ---- 導覽 ----
  const [view, setView] = useState('menu');           // menu | checkout | done
  const [activeCat, setActiveCat] = useState(null);   // scroll-spy 目前分類 id
  const menuScrollY = useRef(0);                      // 返回菜單時恢復捲動位置

  // ---- 購物車 ----
  const [cart, setCart] = useState([]);               // [{key,itemId,name,variantLabel,options,qty,unitPrice,imageUrl,pre}]

  // ---- 品項詳情 sheet ----
  const [sheetItem, setSheetItem] = useState(null);
  const [sheetVariant, setSheetVariant] = useState('');
  const [sheetOptions, setSheetOptions] = useState([]);
  const [sheetQty, setSheetQty] = useState(1);

  // ---- 結帳 ----
  const [pickupDate, setPickupDate] = useState(today);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dayClosed, setDayClosed] = useState(false);
  const [pickupTime, setPickupTime] = useState(null);
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custNote, setCustNote] = useState('');
  const [last5, setLast5] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(null);             // 建單成功回應

  const sectionRefs = useRef({});                     // catId -> element

  // ---- 載入菜單 ----
  const loadMenu = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await bookingService.getTakeoutMenu(slug);
      const cats = Array.isArray(data?.categories) ? data.categories : [];
      setCategories(cats);
      setTakeoutCfg(data?.takeout || null);
      setActiveCat(cats[0]?.id ?? null);
    } catch (err) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  // ---- scroll-spy：捲動時高亮目前分類 ----
  useEffect(() => {
    if (view !== 'menu') return undefined;
    const onScroll = () => {
      let current = null;
      for (const c of categories) {
        const el = sectionRefs.current[c.id];
        if (el && el.getBoundingClientRect().top <= NAV_OFFSET + 40) current = c.id;
      }
      if (current !== null) setActiveCat(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [view, categories]);

  const scrollToCat = (catId) => {
    setActiveCat(catId);
    const el = sectionRefs.current[catId];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // ---- 購物車操作 ----
  const itemQty = (itemId) => cart.filter((l) => l.itemId === itemId).reduce((s, l) => s + l.qty, 0);
  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const cartTotal = cart.reduce((s, l) => s + l.unitPrice * l.qty, 0);
  // 需預訂品項 → 取餐日最早 = 今天 + max(preorder_days)（與後端一致；chips 過早反灰）
  const maxPre = cart.reduce((m, l) => Math.max(m, l.pre || 0), 0);
  const earliestDate = addDaysISO(today, maxPre);
  const preNames = [...new Set(cart.filter((l) => l.pre > 0).map((l) => l.name))];

  const addToCart = (item, variantLabel, options, qty) => {
    const key = lineKey(item.id, variantLabel, options);
    const unitPrice = unitPriceOf(item, variantLabel, options);
    setCart((prev) => {
      const hit = prev.find((l) => l.key === key);
      if (hit) return prev.map((l) => (l.key === key ? { ...l, qty: Math.min(99, l.qty + qty) } : l));
      return [...prev, {
        key, itemId: item.id, name: item.name, variantLabel: variantLabel || '', options: options || [],
        qty, unitPrice, imageUrl: item.imageUrl || null,
        pre: item.kind === 'preorder' ? (item.preorderDays ?? 1) : 0
      }];
    });
  };
  const setLineQty = (key, qty) => {
    setCart((prev) => (qty <= 0 ? prev.filter((l) => l.key !== key) : prev.map((l) => (l.key === key ? { ...l, qty } : l))));
  };

  // ---- 品項詳情 sheet ----
  const openSheet = (item) => {
    const po = item.priceOptions || [];
    setSheetItem(item);
    setSheetVariant(po.length === 1 ? (po[0].label || '') : (po[0]?.label || ''));
    setSheetOptions([]);
    setSheetQty(1);
  };
  const closeSheet = () => setSheetItem(null);
  const toggleSheetOption = (label) =>
    setSheetOptions((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
  const sheetUnit = sheetItem ? unitPriceOf(sheetItem, sheetVariant, sheetOptions) : 0;
  const confirmSheet = () => {
    addToCart(sheetItem, sheetVariant, sheetOptions, sheetQty);
    closeSheet();
  };

  // ---- 結帳導覽（保留/恢復菜單捲動位置） ----
  const gotoCheckout = () => {
    menuScrollY.current = window.scrollY;
    setSubmitError('');
    setView('checkout');
    window.scrollTo(0, 0);
  };
  const backToMenu = () => {
    setView('menu');
    requestAnimationFrame(() => window.scrollTo(0, menuScrollY.current));
  };

  // 取餐日期不可早於需預訂門檻：進結帳/購物車變動時自動校正
  useEffect(() => {
    if (pickupDate < earliestDate) {
      setPickupDate(earliestDate);
      setPickupTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earliestDate]);

  // ---- 取餐時段 ----
  useEffect(() => {
    if (view !== 'checkout' || !takeoutCfg) return undefined;
    let alive = true;
    (async () => {
      setSlotsLoading(true);
      setPickupTime(null);
      try {
        const data = await bookingService.getTakeoutSlots(slug, pickupDate);
        if (!alive) return;
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
        setDayClosed(!!data?.closed);
      } catch (err) {
        if (alive) { setSlots([]); setDayClosed(false); }
      } finally {
        if (alive) setSlotsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [view, pickupDate, slug, takeoutCfg]);

  // 銀行轉帳收款開關（店家可關閉 → 客人取餐時付款，不顯示匯款卡/後五碼；缺省視為開啟）
  const transferOn = takeoutCfg?.bankTransferEnabled !== false;

  // ---- 送單 ----
  const phoneDigits = (custPhone || '').replace(/\D/g, '');
  const canSubmit = cart.length > 0 && !!pickupTime && custName.trim().length > 0
    && phoneDigits.length >= 8 && (!transferOn || /^\d{5}$/.test(last5)) && !submitting;

  const handleSubmit = async () => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const data = await bookingService.createTakeoutOrder(slug, {
        pickupDate,
        pickupTime,
        items: cart.map((l) => ({ itemId: l.itemId, variantLabel: l.variantLabel, options: l.options, qty: l.qty })),
        customerName: custName.trim(),
        customerPhone: phoneDigits,
        customerNote: custNote.trim() || undefined,
        paymentLast5: transferOn ? last5 : undefined
      });
      setDone(data.order);
      setCart([]);
      setView('done');
      window.scrollTo(0, 0);
    } catch (err) {
      const body = err?.response?.data || {};
      const code = err?.response?.status === 429 ? 'RATE_LIMITED' : body.error;
      if (code === 'PREORDER_LEAD') {
        setSubmitError(body.message || '訂單含需預訂品項，請改選較晚的取餐日期');
        if (body.earliestDate) { setPickupDate(body.earliestDate); setPickupTime(null); }
      } else {
        setSubmitError(ERROR_MESSAGES[code] || '送出失敗，請稍後再試');
        if (code === 'BAD_TIME' || code === 'CLOSED_DATE') setPickupTime(null);
        if (code === 'BAD_ITEM' || code === 'BAD_OPTION' || code === 'MARKET_ITEM') loadMenu();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setDone(null);
    setView('menu');
    setPickupTime(null);
    setCustNote('');
    setLast5('');
    setSubmitError('');
    window.scrollTo(0, 0);
  };

  const windowDays = takeoutCfg?.windowDays || 3;
  const dateChoices = useMemo(
    () => Array.from({ length: windowDays }, (_, i) => addDaysISO(today, i)),
    [windowDays, today]
  );

  // ================= 載入 / 錯誤 =================
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: INK }} /></Box>;
  }
  if (loadError || !categories.length) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 2, fontSize: 16 }}>
          {loadError ? '菜單載入失敗，請重新整理' : '店家尚未建立外帶菜單'}
        </Alert>
      </Box>
    );
  }

  // ================= 完成頁 =================
  if (view === 'done' && done) {
    return (
      <Box sx={{ p: { xs: 3, sm: '32px 36px' } }}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography sx={{ fontSize: 16, color: 'text.secondary' }}>已收到您的外帶訂單，取餐代號</Typography>
          <Typography sx={{ fontSize: 56, fontWeight: 900, letterSpacing: 2, color: INK, lineHeight: 1.2 }}>{done.orderNo}</Typography>
          <Chip label="待核帳" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
        </Box>
        <Alert severity="info" sx={{ borderRadius: 2, fontSize: 15, my: 2 }}>
          店家核帳後即開始準備餐點。如有問題店家會來電，取餐時請報「取餐代號＋手機末三碼」。
        </Alert>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
          <Stack spacing={1}>
            <RowKV label="取餐時間" value={`${dateLabel(done.pickupDate, today)} ${done.pickupTime}`} strong />
            <RowKV label="訂購人" value={custName.trim()} />
            <RowKV label={transferOn ? '金額（已回報匯款）' : '金額（取餐時付款）'} value={money(done.total)} strong />
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          {(done.items || []).map((l, i) => (
            <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
              <Typography sx={{ fontSize: 15 }}>
                {l.itemName}{l.variantLabel ? `（${l.variantLabel}）` : ''}
                {l.options?.length ? ` +${l.options.map((o) => o.label).join('、')}` : ''} × {l.qty}
              </Typography>
              <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>{money(l.lineTotal)}</Typography>
            </Stack>
          ))}
        </Box>
        {restaurant?.phone && (
          <Button fullWidth variant="outlined" component="a" href={`tel:${restaurant.phone}`} startIcon={<PhoneIcon />}
            sx={{ mt: 2, color: INK, borderColor: INK, fontSize: 16 }}>
            聯絡店家 {restaurant.phone}
          </Button>
        )}
        <Button fullWidth variant="text" onClick={resetAll} sx={{ mt: 1.5, color: INK, fontSize: 16 }}>再訂一單</Button>
      </Box>
    );
  }

  // ================= 結帳頁 =================
  if (view === 'checkout') {
    return (
      <Box sx={{ p: { xs: 2.5, sm: '24px 36px 40px' } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Button onClick={backToMenu} sx={{ color: INK, fontSize: 16, fontWeight: 700, px: 0.5, minWidth: 0 }}>← 返回菜單</Button>
          <Typography noWrap sx={{ fontWeight: 800, fontSize: 16 }}>{restaurant?.name}</Typography>
        </Stack>
        <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 1.5 }}>檢視外帶訂單</Typography>

        {/* 明細（可改量/移除） */}
        {cart.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 16, mb: 2 }}>購物車是空的，請返回菜單加點餐點</Alert>
        ) : (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1, mb: 1.5 }}>
            {cart.map((l) => (
              <Stack key={l.key} direction="row" alignItems="center" spacing={1.5}
                sx={{ py: 1.2, borderBottom: '1px dashed', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }} noWrap>
                    {l.name}{l.variantLabel ? `（${l.variantLabel}）` : ''}
                  </Typography>
                  {l.options.length > 0 && (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>＋{l.options.join('、')}</Typography>
                  )}
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{money(l.unitPrice)}</Typography>
                </Box>
                {l.imageUrl && (
                  <Box component="img" src={l.imageUrl} alt="" sx={{ width: 56, height: 44, borderRadius: 1.5, objectFit: 'cover' }} />
                )}
                <QtyStepper small value={l.qty} min={0} onChange={(v) => setLineQty(l.key, v)} />
              </Stack>
            ))}
          </Box>
        )}
        <Button fullWidth onClick={backToMenu}
          sx={{ border: '1px dashed', borderColor: 'divider', color: INK, fontSize: 15, fontWeight: 700, py: 1, mb: 3, borderRadius: 2 }}>
          ＋ 加點其他菜色
        </Button>

        {/* 取餐日期（需預訂品項 → 過早日期反灰＋琥珀提示） */}
        <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>取餐日期</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {dateChoices.map((d) => {
            const disabled = d < earliestDate;
            const active = d === pickupDate;
            return (
              <Chip key={d} label={dateLabel(d, today)} disabled={disabled}
                onClick={() => { setPickupDate(d); setPickupTime(null); }}
                sx={{
                  height: 44, fontSize: 15, borderRadius: 2, px: 0.5,
                  bgcolor: active ? INK : '#fff', color: active ? '#fff' : 'text.primary',
                  border: '1px solid', borderColor: active ? INK : 'divider',
                  '&:hover': { bgcolor: active ? INK : '#f0f0f0' }
                }} />
            );
          })}
        </Box>
        {maxPre > 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 14, mb: 2 }}>
            訂單含需預訂品項（{preNames.join('、')}），取餐日期最早為 {dateLabel(earliestDate, today)}
          </Alert>
        )}

        {/* 取餐時間 */}
        <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1, mt: 1 }}>取餐時間</Typography>
        {slotsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} sx={{ color: INK }} /></Box>
        ) : dayClosed || (slots.length > 0 && slots.every((s) => !s.available)) ? (
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 15, mb: 2 }}>本日公休，請另選日期</Alert>
        ) : slots.length === 0 ? (
          <Typography sx={{ fontSize: 15, color: 'text.secondary', mb: 2 }}>
            此日期暫無可取餐時段{pickupDate === today ? '（可能已過今日最後取餐時間）' : ''}
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 1, mb: 2 }}>
            {slots.map((s) => {
              const active = s.time === pickupTime;
              return (
                <Chip key={s.time} label={s.time} disabled={!s.available}
                  onClick={() => s.available && setPickupTime(s.time)}
                  sx={{
                    height: 44, borderRadius: 2, fontSize: 15,
                    textDecoration: s.available ? 'none' : 'line-through',
                    bgcolor: active ? INK : '#fff', color: active ? '#fff' : 'text.primary',
                    border: '1px solid', borderColor: active ? INK : 'divider',
                    '&:hover': { bgcolor: active ? INK : '#f0f0f0' }
                  }} />
              );
            })}
          </Box>
        )}

        {/* 聯絡資料 */}
        <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1, mt: 2 }}>聯絡資料</Typography>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <TextField label="姓名" value={custName} onChange={(e) => setCustName(e.target.value)} fullWidth required
            sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          <TextField label="手機號碼" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} fullWidth required type="tel" placeholder="0912345678"
            sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          <TextField label="備註（選填）" value={custNote} onChange={(e) => setCustNote(e.target.value)} fullWidth multiline minRows={2}
            placeholder="例：不要香菜、餐點請分盒"
            sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
        </Stack>

        {/* 付款方式：轉帳收款開啟 → 匯款卡＋後五碼（Roblox 報名頁模式）；關閉 → 取餐時付款卡 */}
        {transferOn ? (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>付款方式：銀行轉帳</Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, bgcolor: '#fafafa' }}>
              <Stack spacing={0.8}>
                <RowKV label="銀行" value={`${takeoutCfg?.bankInfo?.bankName || ''}${takeoutCfg?.bankInfo?.bankCode ? `（${takeoutCfg.bankInfo.bankCode}）` : ''}`} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>帳號</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, fontFamily: 'monospace' }}>{takeoutCfg?.bankInfo?.accountNumber || ''}</Typography>
                    <IconButton size="small" onClick={() => {
                      if (navigator.clipboard) navigator.clipboard.writeText(takeoutCfg?.bankInfo?.accountNumber || '').catch(() => {});
                      setCopied(true); setTimeout(() => setCopied(false), 1500);
                    }}>
                      <ContentCopyIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                    {copied && <Typography sx={{ fontSize: 12, color: GREEN }}>已複製</Typography>}
                  </Stack>
                </Stack>
                <RowKV label="戶名" value={takeoutCfg?.bankInfo?.accountName || ''} />
                <RowKV label="應匯金額" value={money(cartTotal)} strong />
              </Stack>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 1.2, lineHeight: 1.6 }}>
                請先完成匯款，再回填轉帳帳號<b>後五碼</b>以利店家對帳。{takeoutCfg?.notice ? `（${takeoutCfg.notice}）` : ''}
              </Typography>
            </Box>
            <TextField label="匯款帳號後五碼" value={last5} required fullWidth
              onChange={(e) => setLast5(e.target.value.replace(/\D/g, '').slice(0, 5))}
              inputProps={{ inputMode: 'numeric', maxLength: 5 }}
              placeholder="12345"
              sx={{ mb: 2, '& .MuiInputBase-input': { fontSize: 18, letterSpacing: 4, fontFamily: 'monospace' }, '& label': { fontSize: 16 } }} />
          </>
        ) : (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>付款方式：取餐時付款</Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, bgcolor: '#fafafa' }}>
              <Typography sx={{ fontSize: 14.5, color: 'text.secondary', lineHeight: 1.7 }}>
                請於取餐時向店家付款，應付金額 {money(cartTotal)}。{takeoutCfg?.notice || ''}
              </Typography>
            </Box>
          </>
        )}

        <Typography sx={{ fontSize: 14.5, color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
          點選「送出訂單」即表示您同意將訂單資訊及姓名、電話等聯絡資料，提供予「{restaurant?.name}」及其系統服務商{COMPANY}，作為訂單處理與後續聯繫之用。
        </Typography>

        {submitError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 15 }}>{submitError}</Alert>}

        <Button fullWidth variant="contained" disabled={!canSubmit} onClick={handleSubmit}
          sx={{ py: 1.6, bgcolor: INK, '&:hover': { bgcolor: INK_HOVER }, fontSize: 17, fontWeight: 700 }}>
          {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : `送出訂單 · ${money(cartTotal)}`}
        </Button>
      </Box>
    );
  }

  // ================= 菜單瀏覽（版型 B：左分類欄 + 右品項卡） =================
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* 左側分類欄（sticky，目前分類淡綠底＋綠左槓） */}
        <Box sx={{
          width: 96, flexShrink: 0, position: 'sticky', top: NAV_OFFSET, maxHeight: `calc(100vh - ${NAV_OFFSET}px)`,
          overflowY: 'auto', borderRight: '1px solid', borderColor: 'divider', py: 1
        }}>
          {categories.map((c) => {
            const active = c.id === activeCat;
            return (
              <Box key={c.id} onClick={() => scrollToCat(c.id)}
                sx={{
                  px: 1.2, py: 1.4, cursor: 'pointer', fontSize: 15, lineHeight: 1.3,
                  fontWeight: active ? 800 : 500,
                  color: active ? INK : 'text.secondary',
                  bgcolor: active ? GREEN_BG : 'transparent',
                  borderLeft: '3px solid', borderLeftColor: active ? GREEN : 'transparent',
                  '&:hover': { bgcolor: active ? GREEN_BG : '#f8fafc' }
                }}>
                {c.name}
              </Box>
            );
          })}
        </Box>

        {/* 右側品項卡 */}
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.5, sm: 2.5 }, pb: cartCount ? 10 : 4 }}>
          {categories.map((c) => (
            <Box key={c.id} ref={(el) => { sectionRefs.current[c.id] = el; }} sx={{ pt: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 0.5 }}>{c.name}</Typography>
              {c.items.map((it) => {
                const qty = itemQty(it.id);
                return (
                  <Box key={it.id} onClick={() => openSheet(it)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, py: 1.4, cursor: 'pointer',
                      borderBottom: '1px solid', borderColor: '#f1f5f9', '&:hover': { bgcolor: '#fafafa' }
                    }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}>
                        {it.isSignature && <Box component="span" sx={{ color: '#f59e0b' }}>★ </Box>}
                        {it.name} <Spicy level={it.spicyLevel} />
                      </Typography>
                      {it.note && <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>{it.note}</Typography>}
                      <Typography sx={{ fontSize: 14.5, fontWeight: 600, mt: 0.3 }}>{priceText(it)}</Typography>
                      {it.kind === 'preorder' && (
                        <Chip size="small" label={`需提前 ${it.preorderDays ?? 1} 天預訂`}
                          sx={{ height: 20, fontSize: 11, mt: 0.4, bgcolor: '#f3e8ff', color: '#7e22ce', fontWeight: 700 }} />
                      )}
                    </Box>
                    {it.imageUrl && (
                      <Box component="img" src={it.imageUrl} alt={it.name}
                        sx={{ width: 68, height: 54, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }} />
                    )}
                    {/* ＋ 鈕：入車後直接變數量數字 */}
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: qty > 0 ? INK : '#fff', color: qty > 0 ? '#fff' : INK,
                      border: `1.5px solid ${INK}`, fontSize: qty > 0 ? 15 : 20, fontWeight: 800
                    }}>
                      {qty > 0 ? qty : '＋'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', py: 2 }}>
            時價／季節料理未列於外帶菜單，歡迎來電洽詢{restaurant?.phone ? `：${restaurant.phone}` : ''}
          </Typography>
        </Box>
      </Box>

      {/* 浮動購物車 bar */}
      {cartCount > 0 && (
        <Box sx={{ position: 'sticky', bottom: 0, zIndex: 8, p: 1.5, bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)' }}>
          <Button fullWidth onClick={gotoCheckout}
            sx={{
              bgcolor: INK, color: '#fff', '&:hover': { bgcolor: INK_HOVER },
              py: 1.5, borderRadius: 2.5, fontSize: 16, fontWeight: 700,
              display: 'flex', justifyContent: 'space-between', px: 2.5
            }}>
            <span>{cartCount} 項 · {money(cartTotal)}</span>
            <span>檢視外帶訂單 →</span>
          </Button>
        </Box>
      )}

      {/* 品項詳情 bottom sheet（自適應高度：無選項＝半高、有選項最多 92vh 內捲） */}
      <Drawer anchor="bottom" open={!!sheetItem} onClose={closeSheet}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '92vh', maxWidth: 600, mx: 'auto', left: 0, right: 0 } }}>
        {sheetItem && (
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {sheetItem.imageUrl && (
                <Box component="img" src={sheetItem.imageUrl} alt={sheetItem.name}
                  sx={{ width: '100%', height: 210, objectFit: 'cover', display: 'block' }} />
              )}
              <IconButton onClick={closeSheet}
                sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}>
                <CloseIcon />
              </IconButton>
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                  {sheetItem.isSignature && <Box component="span" sx={{ color: '#f59e0b' }}>★ </Box>}
                  {sheetItem.name} <Spicy level={sheetItem.spicyLevel} />
                </Typography>
                {sheetItem.note && <Typography sx={{ fontSize: 14.5, color: 'text.secondary', mt: 0.3 }}>{sheetItem.note}</Typography>}
                {sheetItem.kind === 'preorder' && (
                  <Alert severity="warning" sx={{ mt: 1.2, borderRadius: 2, fontSize: 13.5, py: 0.3 }}>
                    需提前 {sheetItem.preorderDays ?? 1} 天預訂，結帳時取餐日期將自動限制
                  </Alert>
                )}

                {/* 份量（>1 價才顯示 radio） */}
                {(sheetItem.priceOptions || []).length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15.5, mb: 0.5 }}>份量</Typography>
                    {(sheetItem.priceOptions || []).map((p) => {
                      const on = (p.label || '') === sheetVariant;
                      return (
                        <Stack key={p.label || 'single'} direction="row" alignItems="center" justifyContent="space-between"
                          onClick={() => setSheetVariant(p.label || '')}
                          sx={{ py: 0.6, cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Radio checked={on} size="small" sx={{ '&.Mui-checked': { color: INK } }} />
                            <Typography sx={{ fontSize: 15.5 }}>{p.label || '單一份量'}</Typography>
                          </Stack>
                          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{money(p.price)}</Typography>
                        </Stack>
                      );
                    })}
                  </Box>
                )}

                {/* 加購群組（106 option_groups；未來套餐/加點組合掛此處） */}
                {(sheetItem.optionGroups || []).map((g) => (
                  <Box key={g.name} sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15.5, mb: 0.5 }}>{g.name}</Typography>
                    {(g.options || []).map((o) => {
                      const on = sheetOptions.includes(o.label);
                      return (
                        <Stack key={o.label} direction="row" alignItems="center" justifyContent="space-between"
                          onClick={() => toggleSheetOption(o.label)}
                          sx={{ py: 0.6, cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Checkbox checked={on} size="small" sx={{ '&.Mui-checked': { color: INK } }} />
                            <Typography sx={{ fontSize: 15.5 }}>{o.label}</Typography>
                          </Stack>
                          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>＋{money(o.price)}</Typography>
                        </Stack>
                      );
                    })}
                  </Box>
                ))}

                {/* 數量 */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15.5 }}>數量</Typography>
                  <QtyStepper value={sheetQty} min={1} onChange={setSheetQty} />
                </Stack>
              </Box>
            </Box>
            {/* CTA（固定在 sheet 底部） */}
            <Box sx={{ p: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button fullWidth onClick={confirmSheet}
                sx={{ bgcolor: INK, color: '#fff', '&:hover': { bgcolor: INK_HOVER }, py: 1.5, borderRadius: 2.5, fontSize: 16, fontWeight: 700 }}>
                新增 {sheetQty} 份至購物車 · {money(sheetUnit * sheetQty)}
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

const RowKV = ({ label, value, strong }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: strong ? 17 : 15.5, fontWeight: strong ? 800 : 600 }}>{value}</Typography>
  </Stack>
);

export default TakeoutPanel;
